import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import theme from "theme";
import { colors } from "theme/palette";
import { openURLInNewTab } from "utils/common";
import { AI_DATA_EXTRACTION_ALLOWED_FILE_TYPES } from "utils/constants";
import { reverse } from "utils/urls";

import Box from "components/Box";
import Collapse from "components/Collapse";
import { LinearProgress } from "components/Loading";
import Stack from "components/Stack";
import Tag from "components/Tag";
import Text from "components/Text";
import toast, { DEFAULT_ERROR_MESSAGE } from "components/Toast";
import UploadButton from "components/UploadButton";
import { defaultSmallFileUploadButton } from "components/UploadButton/utils";

import { useDueDiligenceFilters } from "pages/Deal/DueDiligence/Filters";

import { CompanySectionAutomaticFillStatus } from "entities/Checklist/constants";
import { ECompanySettingsFeatureType } from "entities/Company/components/CompanySettings/constants";
import { useCheckFeature } from "entities/Company/components/CompanySettings/utils";
import { useCompanySettings } from "entities/Company/sdk";
import {
  companySectionTriggerAutomaticFill,
  useOnboardingCompanyPackageCategorizedFileFieldCounts,
} from "entities/Deal/sdk";
import ExpandedButton from "entities/Field/components/Filefield/ExpandedButton";
import FilesMenuButton from "entities/Field/components/Filefield/FilesMenuButton";
import FilesTable, {
  IFilefieldCustomValue,
} from "entities/Field/components/Filefield/FilesTable";
import FieldTitle from "entities/Field/components/Title";
import {
  checkFilePermission,
  deleteDueDiligenceFile,
  IField,
  uploadFileToCustomField,
} from "entities/Field/sdk";
import { upload } from "entities/File/sdk";
import { usePackageDealDetails } from "entities/Package/sdk";
import DueDiligenceFieldOpenTaskButton from "entities/Task/components/DueDiligenceFieldOpenTaskButton";
import { ITaskChannel } from "entities/Task/sdk";

import AIExtractionButton from "./AIExtractionButton";

export interface IFilefield {
  editable: boolean;
  field: IField;
  taskChannels: Array<ITaskChannel>;
  packageId: number;
  dealId: number;
  categoryId: number;
  sectionId: number;
  tabId: number;
  onUpdate: ({ message }: { message?: string }) => void;
  companySectionAutomaticFillStatus: CompanySectionAutomaticFillStatus | null;
  companySectionAutomaticFillId: number | null;
  companySectionAutomaticFillFileIds: Array<number>;
  isFocused?: boolean;
  isOnboardingChecklist?: boolean;
}

const Filefield: React.FC<IFilefield> = ({
  editable,
  field,
  taskChannels,
  packageId,
  dealId,
  categoryId,
  sectionId,
  tabId,
  onUpdate,
  companySectionAutomaticFillStatus,
  companySectionAutomaticFillId,
  companySectionAutomaticFillFileIds,
  isFocused = false,
  isOnboardingChecklist = false,
}) => {
  const [isOpenFiles, setIsOpenFiles] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedFileIdsForDataExtraction, setMarkedFileIdsForDataExtraction] =
    useState<Array<number>>(() => {
      return field.custom_values
        .filter(
          (value) =>
            value.file?.is_included_in_company_section_automatic_fill &&
            Object.keys(AI_DATA_EXTRACTION_ALLOWED_FILE_TYPES).includes(
              value.file.file_type
            )
        )
        .flatMap((value) =>
          value.file?.id !== undefined ? [value.file.id] : []
        );
    });
  const { showOnlyFileFields: showOnlyFileFieldsFilterEnabled } =
    useDueDiligenceFilters();

  const isFilesFileTypeCorrect = useCallback(
    ({ fileType }: { fileType: string }) =>
      Object.keys(AI_DATA_EXTRACTION_ALLOWED_FILE_TYPES).includes(fileType),
    []
  );

  const { data: companySettings } = useCompanySettings({ dealId });

  const { mutate: mutatePackage } = usePackageDealDetails({
    packageId,
    dealId,
    queryParams: { tab_id: tabId },
  });

  const { mutate: refetchOnboardingCompanyPackageCategorizedFileFieldCounts } =
    useOnboardingCompanyPackageCategorizedFileFieldCounts({
      dealId,
      enabled:
        isOnboardingChecklist &&
        companySettings?.onboarding_company_package_enabled,
    });

  const enableAIExtractionProcessRestart = useMemo(() => {
    const sortedCurrentlyMarkedFiles = [
      ...selectedFileIdsForDataExtraction,
    ].sort();
    const sortedFilesUsedForLatestCompanySectionAutomaticFill = [
      ...companySectionAutomaticFillFileIds,
    ].sort();

    return (
      sortedCurrentlyMarkedFiles.length !==
        sortedFilesUsedForLatestCompanySectionAutomaticFill.length ||
      !sortedCurrentlyMarkedFiles.every(
        (value, index) =>
          value === sortedFilesUsedForLatestCompanySectionAutomaticFill[index]
      )
    );
  }, [selectedFileIdsForDataExtraction, companySectionAutomaticFillFileIds]);

  const displayProcessingAIExtractionButton = useMemo(() => {
    return (
      (companySectionAutomaticFillStatus ===
        CompanySectionAutomaticFillStatus.PROCESSING &&
        !enableAIExtractionProcessRestart) ||
      isSubmitting
    );
  }, [
    companySectionAutomaticFillStatus,
    enableAIExtractionProcessRestart,
    isSubmitting,
  ]);

  const displayReviewAIExtractionButton = useMemo(() => {
    return (
      companySectionAutomaticFillStatus ===
        CompanySectionAutomaticFillStatus.PROCESSED &&
      !enableAIExtractionProcessRestart
    );
  }, [companySectionAutomaticFillStatus, enableAIExtractionProcessRestart]);

  const displayOpenReviewedAIExtractionButton = useMemo(() => {
    return (
      companySectionAutomaticFillStatus ===
        CompanySectionAutomaticFillStatus.FINISHED &&
      !enableAIExtractionProcessRestart &&
      !_.isEmpty(selectedFileIdsForDataExtraction)
    );
  }, [
    companySectionAutomaticFillStatus,
    enableAIExtractionProcessRestart,
    selectedFileIdsForDataExtraction,
  ]);

  const displayStartAIDataExtractioButton = useMemo(() => {
    return (
      !displayProcessingAIExtractionButton &&
      !displayReviewAIExtractionButton &&
      !displayOpenReviewedAIExtractionButton
    );
  }, [
    displayProcessingAIExtractionButton,
    displayReviewAIExtractionButton,
    displayOpenReviewedAIExtractionButton,
  ]);

  useEffect(() => {
    if (
      field.custom_values.length === 1 &&
      !_.isNil(field.custom_values[0].file) &&
      isFilesFileTypeCorrect({
        fileType: field.custom_values[0].file.file_type,
      })
    ) {
      setMarkedFileIdsForDataExtraction([field.custom_values[0].file.id]);
    }
  }, [
    field.custom_values,
    displayStartAIDataExtractioButton,
    isFilesFileTypeCorrect,
  ]);

  const navigate = useNavigate();

  const customValues = useMemo(
    () =>
      field.custom_values?.map(
        (value) => value as unknown as IFilefieldCustomValue
      ) || [],
    [field]
  );
  const handleFileUpload = useCallback(
    ({ files }: { files: Array<File> }) => {
      setShowLoading(true);

      Promise.all(files.map((file) => upload({ file, isPrivate: true })))
        .then((fileIds) =>
          Promise.all(
            fileIds.map((fileId) =>
              uploadFileToCustomField({
                dealId,
                customFieldId: field.field_id,
                fileId,
              })
            )
          ).then(() => {
            onUpdate({ message: `The file(s) successfully uploaded!` });

            if (
              selectedFileIdsForDataExtraction.length === 1 &&
              field.custom_values.length === 1 &&
              displayStartAIDataExtractioButton
            ) {
              setMarkedFileIdsForDataExtraction([]);
            }
          })
        )
        .catch(() => toast.errorMessage(DEFAULT_ERROR_MESSAGE))
        .finally(() => {
          if (field.custom_values.length === 0) {
            refetchOnboardingCompanyPackageCategorizedFileFieldCounts();
          }
          setShowLoading(false);
        });
    },
    [
      dealId,
      field,
      onUpdate,
      selectedFileIdsForDataExtraction,
      refetchOnboardingCompanyPackageCategorizedFileFieldCounts,
      displayStartAIDataExtractioButton,
    ]
  );

  const handleDeletedFile = useCallback(
    ({ customValue }: { customValue: IFilefieldCustomValue }) => {
      deleteDueDiligenceFile({
        dealId,
        customFieldId: field.field_id,
        customValueId: customValue.value_id,
      })
        .then(() => {
          onUpdate({
            message: `The file ${customValue.file?.original_file_name} has been deleted successfully!`,
          });
          if (
            !_.isNil(customValue.file) &&
            selectedFileIdsForDataExtraction.includes(customValue.file.id)
          ) {
            setMarkedFileIdsForDataExtraction((prevFilesIds) =>
              prevFilesIds.filter((fileId) => fileId !== customValue.file?.id)
            );
          }
        })
        .catch(() => toast.errorMessage(DEFAULT_ERROR_MESSAGE))
        .finally(() =>
          refetchOnboardingCompanyPackageCategorizedFileFieldCounts()
        );
    },
    [
      field,
      dealId,
      onUpdate,
      selectedFileIdsForDataExtraction,
      refetchOnboardingCompanyPackageCategorizedFileFieldCounts,
    ]
  );

  const handleDownload = useCallback(
    ({ fileId }: { fileId: number }) =>
      checkFilePermission({ fileId, fieldId: field.field_id })
        .then(({ url }) => openURLInNewTab({ url }))
        .catch(() => toast.errorMessage(DEFAULT_ERROR_MESSAGE)),
    [field]
  );

  const uploadedLabel = useMemo(() => {
    if (customValues.length === 1) {
      return `${customValues.length} file uploaded`;
    }
    return `${customValues.length} files uploaded`;
  }, [customValues]);

  const handleFileSelectForDataExtraction = useCallback(
    ({ fileId }: { fileId: number }) => {
      setMarkedFileIdsForDataExtraction((prevFiles) => [...prevFiles, fileId]);
    },
    [setMarkedFileIdsForDataExtraction]
  );

  const handleFileSelectForDataExtractionWithFeatureCheck = useCheckFeature({
    feature: ECompanySettingsFeatureType.ONBOARDING_COMPANY_PACKAGE,
    callback: handleFileSelectForDataExtraction,
  });

  const handleFileDeselectForDataExtraction = useCallback(
    ({ fileIdToRemove }: { fileIdToRemove: number }) => {
      if (
        selectedFileIdsForDataExtraction.length === 1 &&
        field.custom_values.length === 1
      ) {
        toast.errorMessage(
          "You cannot unmark this file because at least one file must be selected for processing."
        );
      } else {
        setMarkedFileIdsForDataExtraction((prevFiles) =>
          prevFiles.filter((file) => file !== fileIdToRemove)
        );
      }
    },
    [
      setMarkedFileIdsForDataExtraction,
      field.custom_values.length,
      selectedFileIdsForDataExtraction.length,
    ]
  );

  const handleFileDeselectForDataExtractionWithFeatureCheck = useCheckFeature({
    feature: ECompanySettingsFeatureType.ONBOARDING_COMPANY_PACKAGE,
    callback: handleFileDeselectForDataExtraction,
  });

  const handleOnReviewClick = useCallback(
    () =>
      !_.isNil(companySectionAutomaticFillId)
        ? navigate(
            reverse(URLS.DEAL_PACKAGE_EXTRACTED_DATA_REVIEW, {
              dealId,
              packageId,
              tabId,
              companySectionAutomaticFillId,
            })
          )
        : undefined,
    [dealId, packageId, tabId, companySectionAutomaticFillId, navigate]
  );

  const handleOnReview = useCheckFeature({
    feature: ECompanySettingsFeatureType.ONBOARDING_COMPANY_PACKAGE,
    callback: handleOnReviewClick,
  });

  const handleAIExtractionSubmit = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    companySectionTriggerAutomaticFill({
      dealId,
      fieldId: field.field_id,
      fileIds: selectedFileIdsForDataExtraction.filter(
        (file_id) => !_.isNil(file_id)
      ),
      companySectionId: sectionId,
      companyPackageId: packageId,
      companyTabId: tabId,
    }).then(() => {
      mutatePackage().then(() => {
        setIsSubmitting(false);
        toast.successMessage(
          "The AI data extraction process has started. You will be notified when it is done!"
        );
      });
    });
  }, [
    dealId,
    field.field_id,
    selectedFileIdsForDataExtraction,
    packageId,
    sectionId,
    tabId,
    setIsSubmitting,
    isSubmitting,
    mutatePackage,
  ]);

  const handleSubmit = useCheckFeature({
    feature: ECompanySettingsFeatureType.ONBOARDING_COMPANY_PACKAGE,
    callback: handleAIExtractionSubmit,
  });

  return (
    <>
      {isOnboardingChecklist && !showOnlyFileFieldsFilterEnabled && (
        <Tag
          label="Source file(s)"
          variant="gray20"
          sx={{ display: "inline-block" }}
        />
      )}

      <Box data-testid="dd-field" id={field.field_id.toString()}>
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
          sx={{
            padding: theme.spacing(0.5),
            borderBottom: `1px solid ${colors.blue20}`,
            backgroundColor: isFocused ? colors.green10 : "initial",
          }}
        >
          <FieldTitle
            field={field}
            dealId={dealId}
            packageId={packageId}
            onUpdate={onUpdate}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            {taskChannels.length > 0 && (
              <DueDiligenceFieldOpenTaskButton
                taskChannels={taskChannels}
                sectionId={sectionId}
                categoryId={categoryId}
                company_field={field}
                dealId={dealId}
                tabId={tabId}
                packageId={packageId}
                updatePackage={onUpdate}
                taskButtonDataTestid="dd-open-task-button"
                taskMessagesButtonDataTestid="dd-open-new-task-messages-button"
              />
            )}
            {!field.n_a_status && (
              <Stack flexDirection="row" alignItems="center" gap={1}>
                {customValues.length > 0 && (
                  <>
                    {isOnboardingChecklist &&
                      companySettings?.onboarding_company_package_shown && (
                        <>
                          {displayStartAIDataExtractioButton && (
                            <AIExtractionButton
                              tooltipTitle={
                                _.isEmpty(selectedFileIdsForDataExtraction)
                                  ? "When multiple files are uploaded, please select the source files you want to be used for AI data extraction."
                                  : undefined
                              }
                              disabled={_.isEmpty(
                                selectedFileIdsForDataExtraction
                              )}
                              onClick={handleSubmit}
                              text="Start AI data extraction"
                            />
                          )}
                          {displayProcessingAIExtractionButton && (
                            <AIExtractionButton
                              disabled
                              tooltipTitle="AI is analyzing your files, please wait..."
                              text="Processing"
                            />
                          )}
                          {displayReviewAIExtractionButton && (
                            <AIExtractionButton
                              onClick={handleOnReview}
                              text="Review extracted data"
                            />
                          )}
                          {displayOpenReviewedAIExtractionButton && (
                            <AIExtractionButton
                              color="secondary"
                              text="Open reviewed data"
                              onClick={handleOnReview}
                            />
                          )}
                        </>
                      )}

                    <ExpandedButton
                      label={uploadedLabel}
                      isOpen={isOpenFiles}
                      onClick={() => setIsOpenFiles(!isOpenFiles)}
                    />
                  </>
                )}
                {customValues.length === 0 && (
                  <Tag
                    label="missing"
                    variant="red"
                    dataTestid="empty-field-value"
                  />
                )}
                {editable && (
                  <UploadButton
                    onChange={handleFileUpload}
                    dataTestid="upload-files-input"
                    renderComponent={defaultSmallFileUploadButton}
                    style={{
                      width: "inherit",
                    }}
                  />
                )}
              </Stack>
            )}
            {field.n_a_status && (
              <Text sx={{ opacity: 0.5 }} data-testid="field-value-na">
                N/A
              </Text>
            )}
            <FilesMenuButton
              dealId={dealId}
              packageId={packageId}
              field={field}
              categoryId={categoryId}
              sectionId={sectionId}
              tabId={tabId}
              onUpdate={onUpdate}
              menuButtonDataTestid="dd-field-actions-button"
            />
          </Stack>
        </Stack>
        {showLoading && <LinearProgress sx={{ position: "sticky", left: 0 }} />}

        <Collapse in={isOpenFiles} timeout="auto" unmountOnExit>
          {!field.n_a_status && customValues.length > 0 && (
            <Box
              sx={{
                marginTop: theme.spacing(1),
                paddingLeft: theme.spacing(6),
              }}
            >
              <Tag
                label="file uploads"
                variant="gray20"
                sx={{ display: "inline-block", marginBottom: theme.spacing(1) }}
                dataTestid="uploaded-fields-tag-label"
              />
              <FilesTable
                fieldId={field.field_id}
                sectionId={sectionId}
                packageId={packageId}
                customFieldValues={customValues}
                onDownload={handleDownload}
                onDelete={editable ? handleDeletedFile : undefined}
                onUpdate={() => onUpdate({})}
                selectedFileIdsForDataExtraction={
                  selectedFileIdsForDataExtraction
                }
                handleFileDeselectForDataExtraction={
                  handleFileDeselectForDataExtractionWithFeatureCheck
                }
                handleFileSelectForDataExtraction={
                  handleFileSelectForDataExtractionWithFeatureCheck
                }
                isOnboardingChecklist={isOnboardingChecklist}
              />
            </Box>
          )}
        </Collapse>
      </Box>

      {isOnboardingChecklist && !showOnlyFileFieldsFilterEnabled && (
        <Tag
          label="Data points for extraction"
          variant="gray20"
          sx={{ display: "inline-block" }}
        />
      )}
    </>
  );
};

export default Filefield;
